import { useState } from "react";
import { useAuth } from "@/lib/authContext";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await resetPassword(email);
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a2744] via-[#1e2d4d] to-[#243554] p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-5">
          <div className="inline-flex items-center justify-center bg-white rounded-2xl shadow-2xl px-8 py-6">
            <img 
              src="/logo.png" 
              alt="Carpenter & Company" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <div>
            <h1 className="text-4xl font-heading font-bold text-[#c9a962]">CommandHub</h1>
            <p className="text-white/90 font-medium text-lg mt-1">Operations Platform</p>
          </div>
        </div>

        <Card className="shadow-2xl border-0 bg-white">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-black text-xl font-bold">Reset Password</CardTitle>
            <CardDescription className="text-gray-800">
              Enter your email to receive a password reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-emerald-700 bg-emerald-50 p-4 rounded-md border border-emerald-200">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Check your email</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      We've sent a password reset link to {email}
                    </p>
                  </div>
                </div>
                <Link href="/login">
                  <Button variant="outline" className="w-full border-gray-400 text-black hover:bg-gray-100" data-testid="button-back-to-login">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-black font-semibold">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@carpentercpas.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white border-gray-400 text-black placeholder:text-gray-500"
                    data-testid="input-email"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 p-3 rounded-md border border-red-200" data-testid="text-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-[#1a2744] hover:bg-[#243554] text-white font-medium py-5" 
                  disabled={loading} 
                  data-testid="button-submit"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Reset Link
                </Button>

                <Link href="/login">
                  <Button variant="ghost" className="w-full text-black hover:bg-gray-100" data-testid="button-back">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
